import { z } from 'zod';

import { BooleanQueryParamSchema, IsoDateTimeSchema, UuidSchema } from './common.js';
import { LeadBucketSchema } from './leads.js';

/**
 * Lead reports (Phase 1) - agent-wise tracker over an arbitrary IST date
 * range. Ports the legacy CRM "Reports" page (Query Status / Unattended
 * Status / Hot & Large leads / Carried Forward tables) onto the new lead
 * workflow. Scope follows the same RBAC rules as leads list / dashboard:
 * ADMIN/OPS/FINANCE/READ_ONLY see org-wide, SALES_LEAD sees self + direct
 * reports, SALES_AGENT sees self.
 */

/**
 * Carry-forward cutover floor (IST calendar date). Leads created BEFORE this
 * are never counted as carry-forward: they are the legacy CRM import backlog
 * (~13k OPEN leads reaching back to 2021) that would otherwise bury the real
 * pipeline. Single source of truth shared by the Lead Inbox carry-forward
 * pill/strip AND the Reports carry-forward metric, so the two never drift.
 * (ADR-0042 + 2026-06-23 simplification.)
 */
export const CARRYFORWARD_FLOOR_ISO = '2026-06-01';

const IsoDateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');

export const LeadReportRangeQuerySchema = z.object({
  /** Inclusive IST calendar date (YYYY-MM-DD) the report starts at. */
  from: IsoDateOnly,
  /** Inclusive IST calendar date (YYYY-MM-DD) the report ends at. */
  to: IsoDateOnly,
});
export type LeadReportRangeQuery = z.infer<typeof LeadReportRangeQuerySchema>;

/** Status column metadata so the UI renders whatever statuses exist. */
export const ReportStatusMetaSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  bucket: LeadBucketSchema.nullable(),
  color: z.string().nullable(),
});
export type ReportStatusMeta = z.infer<typeof ReportStatusMetaSchema>;

export const ReportSubStatusMetaSchema = z.object({
  id: UuidSchema,
  statusId: UuidSchema,
  code: z.string(),
  name: z.string(),
});
export type ReportSubStatusMeta = z.infer<typeof ReportSubStatusMetaSchema>;

export const AgentTrackerRowSchema = z.object({
  /** Null only on the synthesized totals row. */
  agentId: UuidSchema.nullable(),
  agentName: z.string(),
  /** Primary role of the agent (null on the totals row). */
  role: z.string().nullable().default(null),
  /** Reporting manager user id - drives the master (SALES_LEAD) → child (agents) grouping. */
  reportingManagerId: UuidSchema.nullable().default(null),
  /** Whether the agent is currently active. Deactivated agents are hidden from
   *  the report tables unless they had real activity in the period. Defaults to
   *  true so an older backend (no field) keeps showing everyone. */
  isActive: z.boolean().default(true),
  /** Leads created in range and visible to the actor. */
  totalLeads: z.number().int().nonnegative(),
  /** Count per QueryStatus code (keys come from `statuses` meta). */
  byStatus: z.record(z.string(), z.number().int().nonnegative()),
  /** Count per QuerySubStatus code (e.g. proposal / warm / hot). */
  bySubStatus: z.record(z.string(), z.number().int().nonnegative()),
  /** Large-lead (adults > LARGE_LEAD_ADULTS_THRESHOLD) by-stage breakup: Cur = created in
   *  range + currently in that stage; Cf = carried-forward cohort currently in that stage. */
  largeBreakup: z
    .object({
      proposalCur: z.number().int().nonnegative().default(0),
      proposalCf: z.number().int().nonnegative().default(0),
      warmCur: z.number().int().nonnegative().default(0),
      warmCf: z.number().int().nonnegative().default(0),
      hotCur: z.number().int().nonnegative().default(0),
      hotCf: z.number().int().nonnegative().default(0),
      wonCur: z.number().int().nonnegative().default(0),
      wonCf: z.number().int().nonnegative().default(0),
    })
    .default({
      proposalCur: 0,
      proposalCf: 0,
      warmCur: 0,
      warmCf: 0,
      hotCur: 0,
      hotCf: 0,
      wonCur: 0,
      wonCf: 0,
    }),
  /** Large-lead Curr/CF counts per destination (per agent), for the Large Lead Destination table. */
  largeDestinations: z
    .array(
      z.object({
        destination: z.string(),
        cur: z.number().int().nonnegative(),
        cf: z.number().int().nonnegative(),
      }),
    )
    .default([]),
  /**
   * Per-agent "Daily Tasks (Today only)" breakdown for the operational Daily
   * Tracker table. LIVE / whole-pool (not scoped to the report period), mirroring
   * the Daily Tracker strip:
   *  - `freshToday` = leads created today (IST), any bucket.
   *  - `todayFu*` = OPEN leads (not not_qualified) whose next follow-up is due
   *    TODAY; `missedFu*` = OPEN leads whose follow-up is OVERDUE (before today);
   *    `revert*` = OPEN leads flagged `isRevertPending`.
   *  Each follow-up leg is split Cur (created this IST month) vs Cf (carried
   *  forward: created in [carry floor, month start)). Leads created before the
   *  carry floor are excluded from the split (carry-floor convention). Total
   *  Tasks (UI) = freshToday + todayFu + missedFu + revert. */
  dailyTasksBreakdown: z
    .object({
      freshToday: z.number().int().nonnegative().default(0),
      todayFuCur: z.number().int().nonnegative().default(0),
      todayFuCf: z.number().int().nonnegative().default(0),
      missedFuCur: z.number().int().nonnegative().default(0),
      missedFuCf: z.number().int().nonnegative().default(0),
      revertCur: z.number().int().nonnegative().default(0),
      revertCf: z.number().int().nonnegative().default(0),
    })
    .default({
      freshToday: 0,
      todayFuCur: 0,
      todayFuCf: 0,
      missedFuCur: 0,
      missedFuCf: 0,
      revertCur: 0,
      revertCf: 0,
    }),
  /** Sum of `saleAmount` (whole agreed amount) on WON-bucket leads. */
  wonSaleAmount: z.number().nonnegative(),
  /** Sum of pre-tax `ttvAmount` on WON-bucket leads - the selling value
   * behind the Business Number TTV / ATV / Shortfall columns. */
  wonTtvAmount: z.number().nonnegative(),
  /** Sum of won saleAmount for leads converted TODAY (IST). Drives the "Today's Sale" tile. Additive; default 0. */
  todaySaleAmount: z.number().nonnegative().default(0),
  /** Open leads with Lead Intent (priority) HIGH or URGENT. Drives the "High Intention" tile. Additive; default 0. */
  highIntentionCount: z.number().int().nonnegative().default(0),
  /** Sum of `ttvAmount` on OPEN-bucket leads (expected value in play). */
  openPipelineAmount: z.number().nonnegative(),
  /** All-leads pipeline TTV (mirrors the large-lead box, no adult filter):
   *  `potentialTtvAll` = Σ ttvAmount over OPEN leads created since the carry
   *  floor (only quoted Active / Long-Follow-up leads carry a ttvAmount, so NQ
   *  contributes 0); `receivedTtvAll` = Σ ttvAmount over leads WON this month. */
  potentialTtvAll: z.number().nonnegative(),
  receivedTtvAll: z.number().nonnegative(),
  /** Avg published itineraries per lead. A "version" = a distinct itinerary
   *  published at any time (published_version set; increments lead-wide - a
   *  frozen published quote + a revision = a NEW itinerary). Deactivated/
   *  soft-deleted-after-publish still counts; never-published don't. Cohort =
   *  leads WORKED this month (>=1 version published in-period); the sum is ALL
   *  their published versions. `won*` = leads WON this month + their total
   *  versions. Kept as sum + count so team totals are a correct weighted mean. */
  itineraryVersionsSum: z.number().int().nonnegative(),
  itineraryVersionsLeadCount: z.number().int().nonnegative(),
  wonItineraryVersionsSum: z.number().int().nonnegative(),
  wonItineraryVersionsLeadCount: z.number().int().nonnegative(),
  /** Quote-timing (Business Numbers columns). Stored as sum-of-seconds + count
   *  so team totals are a correct weighted mean and the average is computed at
   *  render. `firstQuote*` = time from a lead first entering the ACTIVE stage to
   *  the first customer-facing itinerary version (share link), for leads whose
   *  first version was sent in-period. `versionGap*` = every consecutive gap
   *  between published versions where the later version landed in-period. */
  firstQuoteSecondsSum: z.number().nonnegative(),
  firstQuoteCount: z.number().int().nonnegative(),
  versionGapSecondsSum: z.number().nonnegative(),
  versionGapCount: z.number().int().nonnegative(),
  /** Lost Trustability = APPROVED / (APPROVED + REJECTED) lost-approval requests
   *  RAISED this month by the agent (PENDING excluded). Kept as raw counts so
   *  the team score is a correct weighted %. */
  lostApproved: z.number().int().nonnegative(),
  lostRejected: z.number().int().nonnegative(),
  /** Σ days (createdAt -> closedAt) over this agent's leads LOST this month. */
  lossDaysSum: z.number().nonnegative(),
  /** Count of this agent's leads lost this month (closedAt in the month). */
  lostClosedCount: z.number().int().nonnegative(),
  /** Same, split by the status the lead was in immediately BEFORE it was lost
   * (from the stage-history transition into `lost`): NQ (not_qualified) vs
   * Active. Leads with no such history row fall into neither split. */
  nqLossDaysSum: z.number().nonnegative(),
  nqLostCount: z.number().int().nonnegative(),
  activeLossDaysSum: z.number().nonnegative(),
  activeLostCount: z.number().int().nonnegative(),
  /** Same split for leads lost with the auto "Not Contactable Loss(Nq)" reason
   *  (`not_contactable_loss_nq`). Raw sums kept so the org total is a weighted
   *  mean. Default 0 for older payloads. */
  nonContactLossDaysSum: z.number().nonnegative().default(0),
  nonContactLostCount: z.number().int().nonnegative().default(0),
  /** Derived: lossDaysSum / lostClosedCount (rounded 1dp). Null when none lost. */
  avgLossDays: z.number().nonnegative().nullable(),
  /** Derived: avg days-to-loss for leads lost FROM Not Qualified. Null when none. */
  avgLossDaysNq: z.number().nonnegative().nullable(),
  /** Derived: avg days-to-loss for leads lost FROM Active. Null when none. */
  avgLossDaysActive: z.number().nonnegative().nullable(),
  /** Derived: avg days-to-loss for leads lost with the auto "Not Contactable
   *  Loss(Nq)" reason (`not_contactable_loss_nq`). Null when none. */
  avgLossDaysNonContact: z.number().nonnegative().nullable().default(null),
  /** Σ days (createdAt -> closedAt) over this agent's leads WON this month, and
   * the count - kept raw so the org total is a correct weighted mean. */
  convDaysSum: z.number().nonnegative(),
  convCount: z.number().int().nonnegative(),
  /** Derived: convDaysSum / convCount (rounded 1dp) = avg days to conversion.
   * Null when none won this month. */
  avgConversionDays: z.number().nonnegative().nullable(),
  /** Days since this agent's most recent booking (WON lead) - ALL-TIME, NOT
   * scoped to the selected month. Null if the agent has never booked. */
  daysSinceLastBooking: z.number().int().nonnegative().nullable(),
  /** OPEN leads whose follow-up is due today (followupState DUE_TODAY). */
  dueTodayFollowUps: z.number().int().nonnegative(),
  /** OPEN leads with an overdue follow-up (followupState OVERDUE). */
  overdueFollowUps: z.number().int().nonnegative(),
  /** Leads flagged `isRevertPending`. */
  revertPending: z.number().int().nonnegative(),
  /**
   * Live follow-up / revert tiles - LIVE and WHOLE-POOL (not scoped to the
   * report month), matching the Lead Inbox tiles exactly: OPEN leads that are
   * not `not_qualified`, where `today` = nextFollowUpAt is today (IST),
   * `missed` = nextFollowUpAt before today, `revertPending` = isRevertPending.
   * These reflect "right now" work owed, so they don't change with the month.
   */
  liveFollowUps: z.object({
    // Today's / missed work is split by whether the lead has been contacted:
    // `*FollowUp` = active (already-contacted) leads - a real follow-up;
    // `*FirstContact` = Not Qualified (never-contacted) leads - an overdue
    // first touch. The tile headline is the sum of the two.
    todayFollowUp: z.number().int().nonnegative(),
    todayFirstContact: z.number().int().nonnegative(),
    missedFollowUp: z.number().int().nonnegative(),
    missedFirstContact: z.number().int().nonnegative(),
    revertPending: z.number().int().nonnegative(),
  }),
  /** Leads never contacted (`firstContactedAt` null). */
  notContacted: z.number().int().nonnegative(),
  /** Not contacted, created within the last 3 days. */
  freshUnattended: z.number().int().nonnegative(),
  /** Not contacted, created 3-7 days ago. */
  stillUnattended: z.number().int().nonnegative(),
  /** Not contacted, created more than 7 days ago. */
  agedUnattended: z.number().int().nonnegative(),
  /** OPEN large leads (traveller_adults > 5) created in the month - inbox count. */
  largeLeads: z.number().int().nonnegative(),
  /**
   * Large-lead economics (traveller_adults > 5).
   *  - `openCurrent` / `openCarryFwd`: OPEN large leads split by origin -
   *    created this month vs carried in (created >= floor, before the month).
   *    Their sum is the total open large pipeline; the card shows the split.
   *  - `potentialTtv`: Σ ttvAmount over ALL those open large leads.
   *  - `wonThisMonth`: large leads WON this month (`closedAt` in the month, any
   *    created month). Conversion % (UI) = wonThisMonth / (openCurrent +
   *    openCarryFwd) - wins this month against the open large-lead count shown.
   */
  largeLeadStats: z.object({
    openCurrent: z.number().int().nonnegative(),
    openCarryFwd: z.number().int().nonnegative(),
    potentialTtv: z.number().nonnegative(),
    wonThisMonth: z.number().int().nonnegative(),
    /** Σ pre-tax ttvAmount of large leads WON this month - the value actually
     * realised out of the potential. */
    receivedTtv: z.number().nonnegative(),
  }),
  /** OPEN leads created before the 1st of the current IST month. */
  carryForwardOpen: z.number().int().nonnegative(),
  /**
   * Carry-forward, period-correct: leads created before the report `start`
   * whose status was OPEN *as of `start`*, reconstructed from lead_stage_history
   * (not the denormalised currentBucket). This is the backlog the period opens
   * with; Total Pool = carriedInLeads + totalLeads (leads created in-period).
   */
  carriedInLeads: z.number().int().nonnegative(),
  /**
   * Breakdown of the carry-forward (carried-in) cohort - mirrors the Current
   * Month section for the backlog. `currentNq/Active/Lost/Won` = the carried
   * lead's current status (Row A). `currentProposal/Warm/Hot` = its active
   * sub-status now. `mtd*` = carried leads that MOVED INTO that sub-status
   * during the period (`stage_entered_at` inside [start, end]) - a reliable
   * "progress this month" signal, since the backlog has almost no stage history.
   */
  carriedIn: z.object({
    currentNq: z.number().int().nonnegative(),
    currentActive: z.number().int().nonnegative(),
    currentLost: z.number().int().nonnegative(),
    currentWon: z.number().int().nonnegative(),
    currentLongFollowup: z.number().int().nonnegative(),
    currentProposal: z.number().int().nonnegative(),
    currentWarm: z.number().int().nonnegative(),
    currentHot: z.number().int().nonnegative(),
    /** Carried leads that MOVED INTO the Active status this month (entered their
     *  current stage during the period, and are currently active). */
    mtdActive: z.number().int().nonnegative(),
    mtdProposal: z.number().int().nonnegative(),
    mtdWarm: z.number().int().nonnegative(),
    mtdHot: z.number().int().nonnegative(),
    // "Reached ever" for the carried cohort: how many of these still-in-pool
    // leads have EVER reached each active sub-status (in any month), derived
    // from the current funnel position (currently in that stage OR downstream)
    // unioned with any stage-history row - so a carry lead that went warm in a
    // prior month and is still warm/hot counts, even with no history rows.
    reachedProposal: z.number().int().nonnegative(),
    reachedWarm: z.number().int().nonnegative(),
    reachedHot: z.number().int().nonnegative(),
    /** Carried leads that ever reached the Active status (currently active, or
     *  ever entered an active sub-status). */
    reachedActive: z.number().int().nonnegative(),
  }),
  /** Carry-forward count per QueryStatus code (created before this month). */
  carryForwardByStatus: z.record(z.string(), z.number().int().nonnegative()),
  /** OPEN leads with an itinerary/quotation already sent. */
  activeQuotationSent: z.number().int().nonnegative(),
  /** OPEN leads still waiting for a quotation. */
  activeQuotationPending: z.number().int().nonnegative(),
  /** Itinerary coverage of the month's ACTIVE pool (status active, OPEN,
   *  created on/after the carry floor). `activeItinTotal` = the denominator;
   *  published = a sent-to-customer itinerary (`isItinerarySent`); inProgress =
   *  has an itinerary but none sent; none = no itinerary. The three sum to the
   *  total. Default 0 for older payloads. */
  activeItinTotal: z.number().int().nonnegative().default(0),
  activeItinPublished: z.number().int().nonnegative().default(0),
  activeItinInProgress: z.number().int().nonnegative().default(0),
  activeItinNone: z.number().int().nonnegative().default(0),
  /** % of leads moved past first contact. */
  contactabilityPct: z.number().nonnegative(),
  /** WON / total, percent. */
  conversionPct: z.number().nonnegative(),
  /** totalLeads / days in range. */
  avgLeadsPerDay: z.number().nonnegative(),
  /** Active agent target (null when none is set). */
  target: z
    .object({
      amount: z.number().nonnegative(),
      leads: z.number().int().nonnegative().nullable(),
      destination: z.string().nullable(),
    })
    .nullable(),
  /** Distinct leads that EVER entered each sub-status (funnel progression). */
  reachedBySubStatus: z.record(z.string(), z.number().int().nonnegative()),
  /** Distinct leads that ever reached the Active status (any/no sub-status). */
  reachedActive: z.number().int().nonnegative(),
  /**
   * Distinct leads that were CONTACTED = have >= 1 completed follow-up with a
   * "Connected" call status (ever). This is the true "we reached the customer"
   * signal, driving the Contactability metrics - NOT `first_contacted_at`
   * (which only marks the first move into the active pipeline). Reached-Active
   * always nests inside this. Additive; defaults to 0 for older payloads. */
  contactedCount: z.number().int().nonnegative().default(0),
  /**
   * Distinct leads that reached the Active status AND had a customer WhatsApp
   * REPLY (an inbound whatsapp_messages row) on or before the active transition.
   * The "WhatsApp to active" numerator: how many active leads were driven by the
   * customer engaging on WhatsApp (a chat, or replying to a template) rather than
   * only a phone call. Nests inside `reachedActive`. Additive; defaults to 0 for
   * older payloads.
   */
  whatsAppActiveCount: z.number().int().nonnegative().default(0),
  /**
   * Distinct leads that ever entered ANY active sub-status (proposal / warm /
   * hot). This is <= `reachedActive` because a lead can reach the Active status
   * without a sub-status history row ever being written. It is the correct
   * denominator for the total ghost rate: ghosting is a loss FROM a sub-status,
   * so leads that never held one can't contribute to it and must not inflate
   * the base. (Not derivable from `reachedBySubStatus`, whose per-code counts
   * can't be distinct-unioned client-side.)
   */
  reachedActiveSubStatus: z.number().int().nonnegative(),
  /** Distinct leads that ever reached a WON status. */
  reachedWon: z.number().int().nonnegative(),
  /**
   * Funnel-progression cohort INTERSECTIONS (distinct leads in the month
   * cohort that reached BOTH stages), so each funnel-ratio numerator aligns
   * with its denominator and the ratio can never exceed 100%:
   *   Proposal->Warm = proposalAndWarm / reachedBySubStatus[proposal]
   *   Warm->Hot      = warmAndHot      / reachedBySubStatus[warm]
   *   Hot->Won       = hotAndWon       / reachedBySubStatus[hot]
   *   Proposal->Loss = proposalAndLost / reachedBySubStatus[proposal]
   *   Warm->Loss     = warmAndLost     / reachedBySubStatus[warm]
   *   Hot->Loss      = hotAndLost      / reachedBySubStatus[hot]
   * The old form (reachedWon / reachedHot etc.) counted "ever reached the
   * later stage" regardless of passing through the earlier one - a lead that
   * won without ever reaching Hot inflated Hot->Won past 100%.
   */
  funnelReached: z.object({
    proposalAndWarm: z.number().int().nonnegative(),
    warmAndHot: z.number().int().nonnegative(),
    hotAndWon: z.number().int().nonnegative(),
    proposalAndLost: z.number().int().nonnegative(),
    warmAndLost: z.number().int().nonnegative(),
    hotAndLost: z.number().int().nonnegative(),
  }),
  /** LOST transitions grouped by the sub-status they were lost from. */
  lostAtSubStatus: z.record(
    z.string(),
    z.object({
      count: z.number().int().nonnegative(),
      followUps: z.number().int().nonnegative(),
    }),
  ),
  /** TTV vs budget variance buckets (leads with budget > 0 and a TTV). */
  budgetVariance: z.object({
    belowBudget: z.number().int().nonnegative(),
    upTo5: z.number().int().nonnegative(),
    upTo10: z.number().int().nonnegative(),
    moreThan10: z.number().int().nonnegative(),
  }),
  /** Ghosting: leads lost FROM an active sub-status with an "unresponsive
   * client" reason, split by the sub-status they were lost from. */
  ghosting: z.object({
    fromProposal: z.number().int().nonnegative(),
    fromWarm: z.number().int().nonnegative(),
    fromHot: z.number().int().nonnegative(),
  }),
  /** % of leads that reached Active but were then ghosted (lost as an
   * "unresponsive client"): ghostingCount / reachedActive. */
  ghostingPct: z.number().nonnegative(),
  /** Total leads attempted today (follow-up created today, IST) - WHOLE POOL,
   * not scoped to the report month (like the liveFollowUps tiles). */
  attemptedToday: z.number().int().nonnegative(),
  /** Lead counts per BookingTimeline bucket. */
  bookingTimeline: z.record(z.string(), z.number().int().nonnegative()),
  /**
   * Legacy "Performance Insights": per work-category lead counts plus how
   * many of them the agent attempted (logged a follow-up today, IST).
   */
  effort: z.object({
    fresh: z.number().int().nonnegative(),
    freshAttempted: z.number().int().nonnegative(),
    notContactedAttempted: z.number().int().nonnegative(),
    revertPendingAttempted: z.number().int().nonnegative(),
    dueTodayAttempted: z.number().int().nonnegative(),
    overdueAttempted: z.number().int().nonnegative(),
  }),
  /**
   * Whole-pool ("current month + carry forward") live-work cohorts for the
   * Performance tab. Unlike the month-scoped `notContacted` / `revertPending` /
   * `dueTodayFollowUps` / `overdueFollowUps` fields (which stay bounded to leads
   * created in the report period, so they can keep feeding month-only overview
   * metrics like `contactabilityPct`), each of these spans EVERY still-open lead
   * created at/after the carry-forward floor (`CARRYFORWARD_FLOOR_ISO`) - i.e.
   * this month's leads plus everything carried forward. `*Attempted` = of that
   * cohort, how many the agent logged a follow-up on today (IST); the tab's
   * Delta column is the cohort minus its attempted. Fresh stays month-only and
   * is deliberately absent here.
   */
  combinedPool: z.object({
    notContacted: z.number().int().nonnegative(),
    notContactedAttempted: z.number().int().nonnegative(),
    revertPending: z.number().int().nonnegative(),
    revertPendingAttempted: z.number().int().nonnegative(),
    dueToday: z.number().int().nonnegative(),
    dueTodayAttempted: z.number().int().nonnegative(),
    overdue: z.number().int().nonnegative(),
    overdueAttempted: z.number().int().nonnegative(),
  }),
});
export type AgentTrackerRow = z.infer<typeof AgentTrackerRowSchema>;

/**
 * Pivot dimension for the agent-cross reports. `lost_reason` counts
 * LOST-bucket leads by lost reason; `destination` counts all leads by
 * destination master.
 */
export const LeadPivotDimensionSchema = z.enum(['lost_reason', 'destination']);
export type LeadPivotDimension = z.infer<typeof LeadPivotDimensionSchema>;

export const LeadPivotQuerySchema = LeadReportRangeQuerySchema.extend({
  dimension: LeadPivotDimensionSchema,
  /** Restrict to leads created before the 1st of the current IST month. */
  carryForwardOnly: BooleanQueryParamSchema.default(false),
  /**
   * destination dimension only: restrict to the "large lead" cohort (OPEN,
   * created in range, traveller_adults > LARGE_LEAD_ADULTS_THRESHOLD) - the
   * same cohort as the tracker's Large Leads count.
   */
  largeOnly: BooleanQueryParamSchema.default(false),
  /**
   * lost_reason dimension only: restrict to leads that were lost FROM this
   * sub-status (stage-history based; legacy dashboard's radio filter).
   */
  lostFromSubStatusCode: z.string().optional(),
});
export type LeadPivotQuery = z.infer<typeof LeadPivotQuerySchema>;

export const LeadPivotResponseSchema = z.object({
  from: IsoDateOnly,
  to: IsoDateOnly,
  dimension: LeadPivotDimensionSchema,
  carryForwardOnly: z.boolean(),
  largeOnly: z.boolean(),
  /** Row labels (agent names, sorted). */
  rows: z.array(z.string()),
  /** Row agent ids, aligned 1:1 with `rows` - lets the client drill a cell to
   *  that agent's leads. */
  rowIds: z.array(UuidSchema),
  /** Column labels (lost reasons / destinations; "Others" bucket last). */
  columns: z.array(z.string()),
  /** Column ids aligned 1:1 with `columns` (lost reason / destination id);
   *  `null` for the trailing "Others"/"Unspecified" bucket, which isn't
   *  drillable. */
  columnIds: z.array(UuidSchema.nullable()),
  /** cells[rowIndex][colIndex] = lead count. */
  cells: z.array(z.array(z.number().int().nonnegative())),
  rowTotals: z.array(z.number().int().nonnegative()),
  columnTotals: z.array(z.number().int().nonnegative()),
  grandTotal: z.number().int().nonnegative(),
  generatedAt: IsoDateTimeSchema,
});
export type LeadPivotResponse = z.infer<typeof LeadPivotResponseSchema>;

/**
 * Payment pipeline list.
 * - `today`    - OPEN leads whose expected payment date is today (ignores range).
 * - `expected` - all leads whose expected payment date falls inside the range.
 * - `realised` - WON leads that closed on their expected payment date (in range).
 */
export const LeadPipelineKindSchema = z.enum(['today', 'expected', 'realised']);
export type LeadPipelineKind = z.infer<typeof LeadPipelineKindSchema>;

export const LeadPipelineQuerySchema = LeadReportRangeQuerySchema.extend({
  kind: LeadPipelineKindSchema,
});
export type LeadPipelineQuery = z.infer<typeof LeadPipelineQuerySchema>;

export const LeadPipelineRowSchema = z.object({
  leadId: UuidSchema,
  reference: z.string(),
  customerName: z.string().nullable(),
  customerEmail: z.string().nullable(),
  customerPhone: z.string().nullable(),
  agentName: z.string().nullable(),
  /** Assigned agent id - lets the admin group realised pipeline by agent and
   *  reuse the role-based master/child hierarchy from the tracker rows. */
  assignedAgentId: UuidSchema.nullable(),
  statusName: z.string(),
  bucket: LeadBucketSchema,
  destination: z.string().nullable(),
  /** YYYY-MM-DD. */
  expectedPaymentDate: IsoDateOnly,
  /** When the lead entered its current stage (legacy "Date of Booked (Hot)"). */
  stageEnteredAt: IsoDateTimeSchema.nullable(),
  /** When the lead was marked Hot - from lead_stage_history (toSubStatus=hot),
   *  falling back to stageEnteredAt when no history row exists. */
  markedHotAt: IsoDateTimeSchema.nullable(),
  /** Actual booking date (operator-entered `conversionDate`, WON-only). YYYY-MM-DD. */
  bookingDate: IsoDateOnly.nullable(),
  ttvAmount: z.number().nullable(),
  saleAmount: z.number().nullable(),
  closedAt: IsoDateTimeSchema.nullable(),
});
export type LeadPipelineRow = z.infer<typeof LeadPipelineRowSchema>;

export const LeadPipelineResponseSchema = z.object({
  from: IsoDateOnly,
  to: IsoDateOnly,
  kind: LeadPipelineKindSchema,
  rows: z.array(LeadPipelineRowSchema),
  count: z.number().int().nonnegative(),
  totalTtvAmount: z.number().nonnegative(),
  totalSaleAmount: z.number().nonnegative(),
  generatedAt: IsoDateTimeSchema,
});
export type LeadPipelineResponse = z.infer<typeof LeadPipelineResponseSchema>;

/** XLSX payload - base64 + filename, decoded by the admin client (same shape as import templates). */
export const LeadReportExportSchema = z.object({
  filename: z.string(),
  contentBase64: z.string(),
});
export type LeadReportExport = z.infer<typeof LeadReportExportSchema>;

export const AgentTrackerResponseSchema = z.object({
  from: IsoDateOnly,
  to: IsoDateOnly,
  /** Inclusive day count of the range (min 1) - drives avg/day. */
  queryDays: z.number().int().positive(),
  statuses: z.array(ReportStatusMetaSchema),
  subStatuses: z.array(ReportSubStatusMetaSchema),
  rows: z.array(AgentTrackerRowSchema),
  /**
   * Aggregate across all rows (agentId null, agentName "All agents"). For the
   * whole-pool (admin) view the totals ALSO fold in unassigned leads (pool +
   * status counts), so every totals-derived KPI reflects the true monthly lead
   * volume. The per-agent `rows` stay assigned-only (unassigned has no owner).
   */
  totals: AgentTrackerRowSchema,
  /** Unassigned leads created in-period (folded into `totals.totalLeads`). The
   *  assigned-only current-month count is `totals.totalLeads - unassignedLeads`.
   *  0 for scoped (non-admin) viewers, who don't see the unassigned pool. */
  unassignedLeads: z.number().int().nonnegative(),
  /** Unassigned carry-forward leads (folded into `totals.carriedInLeads`). */
  unassignedCarriedIn: z.number().int().nonnegative(),
  /**
   * Unassigned leads that still NEED an owner - OPEN bucket only - for the
   * standalone "Unassigned" tile. Excludes closed (Lost/Won) no-agent leads,
   * which never need assigning. `unassignedLeads` stays the full count for the
   * Total-Pool assigned/unassigned split. Defaulted for staggered-deploy safety.
   */
  unassignedOpenLeads: z.number().int().nonnegative().default(0),
  /** Unassigned OPEN-bucket carry-forward leads (the "Unassigned" tile's carry). */
  unassignedOpenCarriedIn: z.number().int().nonnegative().default(0),
  generatedAt: IsoDateTimeSchema,
});
export type AgentTrackerResponse = z.infer<typeof AgentTrackerResponseSchema>;

/**
 * Day-wise Performance Insights ("follow-up productivity") view. The main
 * tracker's Performance tab is a live "today" snapshot (current-state cohorts +
 * today's attempt window); this view instead reconstructs a SINGLE IST calendar
 * day purely from real timestamps (lead.createdAt, follow-up scheduled/created/
 * completed times), so any PAST day reads accurately. It intentionally drops the
 * live-only backlog cohorts (Not Contacted / Revert Pending) that have no
 * per-day history and only make sense "as of now".
 */
export const PerformanceDayQuerySchema = z.object({
  /** IST calendar date (YYYY-MM-DD) to reconstruct the day's productivity for. */
  date: IsoDateOnly,
});
export type PerformanceDayQuery = z.infer<typeof PerformanceDayQuerySchema>;

export const PerformanceDayRowSchema = z.object({
  /** Null only on the synthesized totals row. */
  agentId: UuidSchema.nullable(),
  agentName: z.string(),
  role: z.string().nullable().default(null),
  reportingManagerId: UuidSchema.nullable().default(null),
  isActive: z.boolean().default(true),
  /** Leads created on the chosen IST day (assigned to this agent). */
  freshLeads: z.number().int().nonnegative(),
  /** Of those fresh leads, how many got a first touch (a follow-up logged) the
   *  same day. */
  freshTouched: z.number().int().nonnegative(),
  /** Follow-ups whose scheduled/due time falls on the chosen day. */
  dueFollowUps: z.number().int().nonnegative(),
  /** Of the day's due follow-ups, how many were completed (at any time). */
  dueCompleted: z.number().int().nonnegative(),
  /** Follow-ups due on/before the chosen day and still open at that day's end -
   *  the "missed" backlog as of the chosen day (bounded at the carry-forward
   *  floor). */
  missedFollowUps: z.number().int().nonnegative(),
  /** Total follow-ups the agent logged (created) on the chosen day - raw effort. */
  attemptsLogged: z.number().int().nonnegative(),
  /** Distinct leads the agent touched (logged a follow-up on) that day. */
  leadsTouched: z.number().int().nonnegative(),
});
export type PerformanceDayRow = z.infer<typeof PerformanceDayRowSchema>;

export const PerformanceDayResponseSchema = z.object({
  date: IsoDateOnly,
  rows: z.array(PerformanceDayRowSchema),
  /** Aggregate across all rows (agentId null, agentName "All agents"). */
  totals: PerformanceDayRowSchema,
  generatedAt: IsoDateTimeSchema,
});
export type PerformanceDayResponse = z.infer<typeof PerformanceDayResponseSchema>;
